import { createAsyncThunk } from "@reduxjs/toolkit";
import { MakeApiRequest, toApiError, ApiError } from "App/common/Http";
import { showToast } from "App/store/slices/toast.slice";

type ServerType = "login" | "taxi" | "payment";

interface ApiThunkConfig<Arg> {
  server: ServerType;
  /** Static path, or a builder when the path depends on the argument. */
  url: string | ((arg: Arg) => string);
  method?: "GET" | "POST" | "PUT" | "DELETE";
  /** Toast the server's `msg` on success — same flag the web app's Http used. */
  successMsg?: boolean;
  /** Toast the server's `msg` on failure. */
  errorMsg?: boolean;
  /** Build the request body from the argument. Defaults to the argument itself. */
  body?: (arg: Arg) => Record<string, any> | null;
  /** Build the query string from the argument. */
  params?: (arg: Arg) => Record<string, any> | undefined;
  headers?: Record<string, string>;
}

/**
 * One factory for every network call in the app, so each slice declares only
 * *what* it fetches rather than repeating the try/catch, the toast dispatch and
 * the error normalisation.
 *
 * The web app funnelled all of this through `MakeApiRequest`, which dispatched
 * `${TYPE}_PENDING|SUCCESS|FAILURE` by hand. createAsyncThunk already emits that
 * exact triplet as `pending`/`fulfilled`/`rejected`, so the semantics carry over
 * unchanged — only the boilerplate goes away.
 *
 * Rejections always carry an `ApiError`, so a slice's rejected case can read
 * `action.payload.msg` without narrowing.
 */
export const apiThunk = <Returned = any, Arg = void>(
  typePrefix: string,
  config: ApiThunkConfig<Arg>
) =>
  createAsyncThunk<Returned, Arg, { rejectValue: ApiError }>(
    typePrefix,
    async (arg, { dispatch, rejectWithValue }) => {
      const {
        server,
        url,
        method = "GET",
        successMsg = false,
        errorMsg = false,
        body,
        params,
        headers,
      } = config;

      try {
        const resolvedUrl = typeof url === "function" ? url(arg) : url;
        // GET/DELETE carry no body; for the others default to the raw argument.
        const data =
          method === "GET" || method === "DELETE"
            ? null
            : body
            ? body(arg)
            : ((arg ?? null) as Record<string, any> | null);

        const response = await MakeApiRequest({
          server,
          url: resolvedUrl,
          method,
          data,
          params: params ? params(arg) : undefined,
          headers,
        });

        if (successMsg && response?.msg) {
          dispatch(showToast({ type: "success", head: "Success", text: response.msg }));
        }

        return response as Returned;
      } catch (error: any) {
        console.error("API Request Error:", error);
        const apiError = toApiError(error);

        if (errorMsg) {
          dispatch(showToast({ type: "error", head: "Error", text: apiError.msg }));
        }

        return rejectWithValue(apiError);
      }
    }
  );
